import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SupplierBillingStatementService from '../../services/SupplierBillingStatementService';
import { PAYMENT_METHOD_LABELS } from '../../components/Reposition/EditReplenishmentModal';
import { formatDate } from '../../utils/DateUtils';
import { formatMoney } from '../../utils/currency';

const MAX_PENDING_INVOICES = 100;

const todayInputValue = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const round2 = (value) => Math.round(value * 100) / 100;

// Pago a un proveedor: se eligen las facturas pendientes a las que corresponde y el pago se reparte
// de la más vieja a la más nueva.
const SupplierPaymentModal = ({ open, onClose, onSaved, initialSupplier = null }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [totalPendingInvoices, setTotalPendingInvoices] = useState(0);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [amount, setAmount] = useState('');
  const [amountTouched, setAmountTouched] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayInputValue());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Proveedores con deuda: solo ellos tienen facturas pendientes
  useEffect(() => {
    if (!open) return;
    setSupplier(initialSupplier);
    setInvoices([]);
    setSelectedIds([]);
    setAmount('');
    setAmountTouched(false);
    setPaymentMethod('');
    setPaymentDate(todayInputValue());
    setNotes('');
    setError('');
    SupplierBillingStatementService.getSupplierBillingStatements(1, 500)
      .then((response) => setSuppliers(response.statements || []))
      .catch(() => setSuppliers([]));
  }, [open, initialSupplier]);

  useEffect(() => {
    if (!open || !supplier?.supplierId) {
      setInvoices([]);
      return;
    }
    let cancelled = false;
    setLoadingInvoices(true);
    setSelectedIds([]);
    setAmountTouched(false);
    SupplierBillingStatementService.getSupplierReplenishments(supplier.supplierId, 1, MAX_PENDING_INVOICES, true)
      .then((response) => {
        if (cancelled) return;
        // Más viejas primero: es el orden en que se aplica el pago
        const rows = [...(response.replenishments || [])].sort(
          (a, b) => new Date(a.replenishmentDate) - new Date(b.replenishmentDate) || a.id - b.id
        );
        setInvoices(rows);
        setTotalPendingInvoices(response.totalCount || rows.length);
      })
      .catch(() => {
        if (!cancelled) setInvoices([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingInvoices(false);
      });
    return () => { cancelled = true; };
  }, [open, supplier?.supplierId]);

  const selectedInvoices = useMemo(
    () => invoices.filter((invoice) => selectedIds.includes(invoice.id)),
    [invoices, selectedIds]
  );
  const selectedPending = round2(selectedInvoices.reduce((sum, invoice) => sum + invoice.pendingAmount, 0));

  // Mientras no se edite el monto, se paga todo lo seleccionado
  useEffect(() => {
    if (!amountTouched) setAmount(selectedPending > 0 ? String(selectedPending) : '');
  }, [selectedPending, amountTouched]);

  const amountValue = parseFloat(amount);
  const amountError = amount !== '' && (
    !(amountValue > 0)
      ? 'Ingresá un monto mayor a 0'
      : amountValue > selectedPending + 0.005
        ? `No puede superar lo pendiente de las facturas elegidas (${formatMoney(selectedPending)})`
        : ''
  );

  // Cómo se reparte el pago entre las facturas elegidas
  const allocation = useMemo(() => {
    let left = amountValue > 0 && !amountError ? amountValue : 0;
    const result = {};
    selectedInvoices.forEach((invoice) => {
      const applied = Math.min(left, invoice.pendingAmount);
      if (applied > 0.004) result[invoice.id] = round2(applied);
      left = round2(left - applied);
    });
    return result;
  }, [selectedInvoices, amountValue, amountError]);

  const canSave = Boolean(supplier) && selectedIds.length > 0 && amountValue > 0 && !amountError
    && Boolean(paymentMethod) && Boolean(paymentDate);

  const toggleInvoice = (id) => {
    setSelectedIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  };
  const allSelected = invoices.length > 0 && selectedIds.length === invoices.length;
  const toggleAll = () => setSelectedIds(allSelected ? [] : invoices.map((invoice) => invoice.id));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await SupplierBillingStatementService.registerSupplierPayment(supplier.supplierId, {
        replenishmentIds: selectedIds,
        amount: round2(amountValue),
        paymentMethod,
        // Fecha + hora actual para que el orden de los movimientos sea estable
        paymentDate: `${paymentDate}T${new Date().toTimeString().slice(0, 8)}`,
        notes: notes.trim() || undefined
      });
      onSaved?.();
      onClose();
    } catch (e) {
      console.error('Error registering supplier payment:', e);
      setError(e?.response?.data?.error_message || 'No se pudo registrar el pago');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Registrar pago a proveedor</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Autocomplete
            options={suppliers}
            value={supplier}
            onChange={(_, value) => setSupplier(value)}
            getOptionLabel={(option) => option?.supplierName || ''}
            isOptionEqualToValue={(option, value) => option.supplierId === value.supplierId}
            renderInput={(params) => <TextField {...params} label="Proveedor" />}
            noOptionsText="No hay proveedores con deuda pendiente"
          />

          {supplier && (
            <>
              <Typography variant="subtitle2">
                Facturas pendientes: elegí a cuáles corresponde este pago
              </Typography>
              {loadingInvoices ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
              ) : invoices.length === 0 ? (
                <Alert severity="info">Este proveedor no tiene facturas pendientes.</Alert>
              ) : (
                <>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={allSelected}
                              indeterminate={selectedIds.length > 0 && !allSelected}
                              onChange={toggleAll}
                            />
                          </TableCell>
                          <TableCell>Factura</TableCell>
                          <TableCell align="center">Fecha</TableCell>
                          <TableCell align="center">Vencimiento</TableCell>
                          <TableCell align="right">Monto</TableCell>
                          <TableCell align="right">Pendiente</TableCell>
                          <TableCell align="right">Este pago</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoices.map((invoice) => {
                          const selected = selectedIds.includes(invoice.id);
                          return (
                            <TableRow key={invoice.id} hover selected={selected} onClick={() => toggleInvoice(invoice.id)} sx={{ cursor: 'pointer' }}>
                              <TableCell padding="checkbox"><Checkbox checked={selected} /></TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{invoice.invoice || `Reposición #${invoice.id}`}</TableCell>
                              <TableCell align="center">{formatDate(invoice.replenishmentDate)}</TableCell>
                              <TableCell align="center">{formatDate(invoice.dueDate)}</TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatMoney(invoice.amount)}</TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatMoney(invoice.pendingAmount)}</TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                {allocation[invoice.id] ? formatMoney(allocation[invoice.id]) : '-'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {totalPendingInvoices > invoices.length && (
                    <Alert severity="warning">
                      Se muestran las {invoices.length} facturas pendientes más viejas de {totalPendingInvoices}.
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                      label="Monto del pago"
                      type="number"
                      value={amount}
                      onChange={(e) => { setAmount(e.target.value); setAmountTouched(true); }}
                      error={Boolean(amountError)}
                      helperText={amountError || (selectedIds.length > 0 ? `Pendiente de lo elegido: ${formatMoney(selectedPending)}` : 'Elegí al menos una factura')}
                      inputProps={{ min: 0, step: '0.01' }}
                      disabled={selectedIds.length === 0}
                      sx={{ flex: 1, minWidth: 200 }}
                    />
                    <FormControl sx={{ flex: 1, minWidth: 180 }}>
                      <InputLabel id="supplier-payment-method-label">Forma de pago</InputLabel>
                      <Select
                        labelId="supplier-payment-method-label"
                        value={paymentMethod}
                        label="Forma de pago"
                        onChange={(e) => setPaymentMethod(e.target.value)}
                      >
                        {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                          <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Fecha del pago"
                      type="date"
                      value={paymentDate}
                      onChange={(e) => setPaymentDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1, minWidth: 180 }}
                    />
                  </Box>
                  <TextField
                    label="Notas (opcional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    fullWidth
                  />
                </>
              )}
            </>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <LoadingButton variant="contained" loading={saving} disabled={!canSave} onClick={handleSave}>
          Registrar pago
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SupplierPaymentModal;
